import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import type { CustomerDocument } from "../../shared/customer-documents";
import type { CustomerContextSummary } from "../../shared/customer-record-schemas";
import type { HubRecord, LibraryHubAssignment, ToolHub } from "../../shared/tool-hubs";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Sheet, SheetBody, SheetFooter } from "../components/ui/sheet";
import { documentDate } from "../knowledge/customer-document-format";
import { CustomerDocumentDialog } from "./CustomerDocumentDialog";
import { dayOf } from "./customer-page-format";
import { getCustomerLibraryDocument, listCustomerLibrary } from "./customer-documents-api";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { customerPageKeys, linkLibraryDocumentToHub } from "./tool-hubs-api";

type Person = CustomerContextSummary["people"][number];

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl bg-card p-6 shadow-card" aria-label={title}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-bold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-muted py-2.5 last:border-b-0">
      <dt className="text-sm font-semibold text-muted-foreground">{label}</dt>
      <dd className="m-0 text-right">{children}</dd>
    </div>
  );
}

function Unset({ text, action }: { text: string; action: ReactNode }) {
  return (
    <span className="inline-flex flex-wrap items-baseline justify-end gap-x-2">
      <span className="text-muted-foreground">{text}</span>
      {action}
    </span>
  );
}

export function ToolFacts({
  hub,
  contact,
  customerSlug,
  canWrite,
  navigate,
  onEdit,
}: {
  hub: ToolHub;
  contact: Person | null;
  customerSlug: string;
  canWrite: boolean;
  navigate: OperationsNavigate;
  onEdit: () => void;
}) {
  const setDate = canWrite ? (
    <Button variant="link" className="min-h-0 p-0" onClick={onEdit}>
      Set date
    </Button>
  ) : null;
  return (
    <section className="rounded-xl bg-card px-6 py-3 shadow-card" aria-label="About this tool">
      <dl className="m-0">
        <Fact label="Go-live">
          {hub.goLiveOn ? documentDate(hub.goLiveOn) : <Unset text="Not set" action={setDate} />}
        </Fact>
        <Fact label="Kickoff">
          {hub.kickoffOn ? (
            documentDate(hub.kickoffOn)
          ) : (
            <Unset text="Not scheduled" action={setDate} />
          )}
        </Fact>
        <Fact label="Deployed">{hub.deployed ? "Yes" : "Not yet"}</Fact>
        <Fact label="Customer contact">
          {contact ? (
            contact.displayName
          ) : (
            <OperationsLink
              className="font-semibold text-link"
              href={`/customers/${customerSlug}/people`}
              navigate={navigate}
            >
              Choose one
            </OperationsLink>
          )}
        </Fact>
      </dl>
    </section>
  );
}

export function ToolDecisions({ decisions }: { decisions: readonly HubRecord[] }) {
  return (
    <Panel title="Current decisions">
      {decisions.length ? (
        <ul className="space-y-3">
          {decisions.slice(0, 8).map((decision) => (
            <li key={decision.id}>
              <span className="block font-semibold">{decision.title}</span>
              <span className="text-sm text-muted-foreground">{dayOf(decision.occurredAt)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">
          Nothing decided yet. Log a decision when something is agreed.
        </p>
      )}
    </Panel>
  );
}

/** Library documents assigned to this tool; knowledge can be assigned from here. */
export function ToolKnowledge({
  organizationId,
  customerSlug,
  customerName,
  hub,
  assigned,
  canAssign,
  canEditCommercial,
  canUsePages,
}: {
  organizationId: string;
  customerSlug: string;
  customerName: string;
  hub: ToolHub;
  assigned: readonly LibraryHubAssignment[];
  canAssign: boolean;
  canEditCommercial: boolean;
  canUsePages: boolean;
}) {
  const [picking, setPicking] = useState(false);
  const [open, setOpen] = useState<CustomerDocument | null>(null);
  const read = useMutation({
    mutationFn: (documentId: string) => getCustomerLibraryDocument(customerSlug, documentId),
    onSuccess: (result) => {
      setOpen(result.document);
    },
  });
  return (
    <Panel
      title="Knowledge"
      action={
        canAssign ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setPicking(true);
            }}
          >
            Add
          </Button>
        ) : null
      }
    >
      <AssignedList
        assigned={assigned}
        customerName={customerName}
        onOpen={(documentId) => {
          read.mutate(documentId);
        }}
      />
      {read.error ? <FieldError>{read.error.message}</FieldError> : null}
      <KnowledgePicker
        open={picking}
        organizationId={organizationId}
        customerSlug={customerSlug}
        hub={hub}
        assigned={assigned}
        onClose={() => {
          setPicking(false);
        }}
      />
      {open ? (
        <CustomerDocumentDialog
          slug={customerSlug}
          customerName={customerName}
          kind={open.draft.fields.kind}
          initial={open}
          canWrite={open.draft.fields.kind === "knowledge" ? canAssign : canEditCommercial}
          canUsePages={canUsePages}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

function AssignedList({
  assigned,
  customerName,
  onOpen,
}: {
  assigned: readonly LibraryHubAssignment[];
  customerName: string;
  onOpen: (documentId: string) => void;
}) {
  if (!assigned.length)
    return <p className="text-muted-foreground">No knowledge assigned to this tool yet.</p>;
  return (
    <ul className="space-y-1">
      {assigned.map((row) => (
        <li key={row.documentId}>
          <button
            type="button"
            className="block w-full rounded-md py-2 text-left outline-none hover:text-link focus-visible:shadow-[var(--focus-ring)]"
            onClick={() => {
              onOpen(row.documentId);
            }}
          >
            <span className="block font-semibold">{row.title}</span>
            <span className="text-sm text-muted-foreground">
              {row.shared ? `Shared with ${customerName}` : "Team only"}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function KnowledgePicker({
  open,
  organizationId,
  customerSlug,
  hub,
  assigned,
  onClose,
}: {
  open: boolean;
  organizationId: string;
  customerSlug: string;
  hub: ToolHub;
  assigned: readonly LibraryHubAssignment[];
  onClose: () => void;
}) {
  const client = useQueryClient();
  const library = useQuery({
    queryKey: ["ops", "customer-library", customerSlug, "knowledge-picker"],
    queryFn: () => listCustomerLibrary(customerSlug, { kind: "knowledge", archived: "false" }),
    enabled: open,
  });
  const assign = useMutation({
    mutationFn: (documentId: string) =>
      linkLibraryDocumentToHub(organizationId, documentId, hub.id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: customerPageKeys.assignments(organizationId) }),
  });
  const here = new Set(assigned.map((row) => row.documentId));
  const choices = (library.data?.documents ?? []).filter((document) => !here.has(document.id));
  return (
    <Sheet open={open} onClose={onClose} title="Add knowledge" kicker={hub.name}>
      <SheetBody>
        <p className="text-muted-foreground">
          Choose articles from the customer’s library. Each article belongs to one tool at a time.
        </p>
        {library.error ? <FieldError>{library.error.message}</FieldError> : null}
        {assign.error ? <FieldError>{assign.error.message}</FieldError> : null}
        {library.data && !choices.length ? (
          <p className="text-muted-foreground">Every knowledge article is already here.</p>
        ) : null}
        <ul className="divide-y divide-muted">
          {choices.map((document) => (
            <li key={document.id} className="flex items-center justify-between gap-3 py-3">
              <span className="min-w-0">
                <span className="block font-semibold">{document.draft.fields.title}</span>
                <span className="text-sm text-muted-foreground">
                  {document.published ? "Shared with the customer" : "Team only"}
                </span>
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={assign.isPending}
                onClick={() => {
                  assign.mutate(document.id);
                }}
              >
                Add to {hub.name}
              </Button>
            </li>
          ))}
        </ul>
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </SheetFooter>
    </Sheet>
  );
}
